import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { MOCK_PROJECTS, mockCrud, loadFromStorage } from '@/services/mockData'
import { TIER_FEATURES } from '@/services/stripe'
import { getNextTierName } from '@/composables/useTierLimits'
import type { Project, ProjectFormData, ProjectStatus, ProjectFile, UserFile } from '@/types'

const STORAGE_KEY = 'projects'

export const useProjectStore = defineStore('project', () => {
  const projects = ref<Project[]>([])
  const currentProject = ref<Project | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')

  const activeProjects = computed(() => projects.value.filter(p => p.status === 'in_progress'))
  const completedProjects = computed(() => projects.value.filter(p => p.status === 'completed'))

  const projectsByStatus = computed(() => {
    const grouped: Record<ProjectStatus, Project[]> = {} as Record<ProjectStatus, Project[]>
    const statuses: ProjectStatus[] = ['planning', 'in_progress', 'completed', 'on_hold', 'cancelled']
    statuses.forEach(status => {
      grouped[status] = projects.value.filter(p => p.status === status)
    })
    return grouped
  })

  const totalFilamentUsed = computed(() => projects.value.reduce((sum, p) => sum + p.total_filament_used, 0))
  const totalPrintTime = computed(() => projects.value.reduce((sum, p) => sum + p.total_print_time, 0))

  const overallSuccessRate = computed(() => {
    const total = projects.value.reduce((sum, p) => sum + p.total_prints, 0)
    const successful = projects.value.reduce((sum, p) => sum + p.successful_prints, 0)
    return total > 0 ? (successful / total) * 100 : 0
  })

  const getById = (id: string) => projects.value.find(p => p.id === id)

  const fetchProjects = async (force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) {
      console.warn('[projectStore] fetchProjects called without userId')
      return
    }

    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 200))
        projects.value = loadFromStorage(STORAGE_KEY, MOCK_PROJECTS)
      } else {
        const { supabase } = await import('@/services/supabase')
        let query = supabase
          .from('projects')
          .select('*')
        if (teamId) {
          query = query.eq('team_id', teamId)
        } else {
          query = query.is('team_id', null).eq('user_id', authStore.userId)
        }
        const { data, error: fetchError } = await query
          .order('updated_at', { ascending: false })

        if (fetchError) throw fetchError
        projects.value = data as Project[]
      }
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch projects'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const clearData = () => {
    projects.value = []
    currentProject.value = null
    _loaded.value = false
  }

  const fetchProject = async (id: string) => {
    loading.value = true
    try {
      const project = getById(id)
      if (project) {
        currentProject.value = project
        return { success: true, data: project }
      }
      return { success: false, error: 'Project not found' }
    } finally {
      loading.value = false
    }
  }

  const createProject = async (projectData: ProjectFormData, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    // Tier limit check (skip for team context)
    if (!teamId) {
      const tierConfig = TIER_FEATURES[authStore.subscriptionTier]
      const limit = tierConfig.projects
      if (limit !== -1 && projects.value.length >= limit) {
        const nextTier = getNextTierName(authStore.subscriptionTier)
        const upgradeHint = nextTier ? ` Upgrade to ${nextTier} for more.` : ''
        return { success: false, error: `You've reached the ${tierConfig.name} plan limit of ${limit} projects.${upgradeHint}` }
      }
    }

    loading.value = true
    try {
      if (authStore.isDemoMode) {
        const newProject = mockCrud.create(STORAGE_KEY, {
          ...projectData,
          user_id: authStore.userId,
          total_prints: 0,
          successful_prints: 0,
          failed_prints: 0,
          total_filament_used: 0,
          total_print_time: 0,
          estimated_cost: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as Project, projects.value)
        projects.value.unshift(newProject)
        return { success: true, data: newProject }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('projects')
          .insert({
            ...projectData,
            user_id: authStore.userId,
            ...(teamId ? { team_id: teamId } : {}),
            total_prints: 0,
            successful_prints: 0,
            failed_prints: 0,
            total_filament_used: 0,
            total_print_time: 0,
            estimated_cost: 0
          })
          .select()
          .single()

        if (insertError) throw insertError
        projects.value.unshift(data as Project)
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create project'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateProject = async (id: string, updates: Partial<ProjectFormData>) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, updates, projects.value)
        if (updated) {
          const index = projects.value.findIndex(p => p.id === id)
          if (index !== -1) projects.value[index] = updated
          if (currentProject.value?.id === id) currentProject.value = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('projects')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = projects.value.findIndex(p => p.id === id)
        if (index !== -1) projects.value[index] = data as Project
        if (currentProject.value?.id === id) currentProject.value = data as Project
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update project'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deleteProject = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(STORAGE_KEY, id, projects.value)
        projects.value = projects.value.filter(p => p.id !== id)
        if (currentProject.value?.id === id) currentProject.value = null
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase.from('projects').delete().eq('id', id)
        if (deleteError) throw deleteError
        projects.value = projects.value.filter(p => p.id !== id)
        if (currentProject.value?.id === id) currentProject.value = null
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete project'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateStatus = async (id: string, status: ProjectStatus) => {
    const updates: Partial<ProjectFormData> = { status }
    if (status === 'completed') updates.completed_date = new Date().toISOString()
    else if (status === 'in_progress' && !getById(id)?.start_date) updates.start_date = new Date().toISOString()
    return updateProject(id, updates)
  }

  const addPrintToProject = async (projectId: string, success: boolean, filamentUsed: number, printTime: number) => {
    const project = getById(projectId)
    if (!project) return { success: false, error: 'Project not found' }

    return updateProject(projectId, {
      total_prints: project.total_prints + 1,
      successful_prints: success ? project.successful_prints + 1 : project.successful_prints,
      failed_prints: success ? project.failed_prints : project.failed_prints + 1,
      total_filament_used: project.total_filament_used + filamentUsed,
      total_print_time: project.total_print_time + printTime
    } as any)
  }

  const removePrintFromProject = async (projectId: string, success: boolean, filamentUsed: number, printTime: number) => {
    const project = getById(projectId)
    if (!project) return { success: false, error: 'Project not found' }

    return updateProject(projectId, {
      total_prints: Math.max(0, project.total_prints - 1),
      successful_prints: success ? Math.max(0, project.successful_prints - 1) : project.successful_prints,
      failed_prints: success ? project.failed_prints : Math.max(0, project.failed_prints - 1),
      total_filament_used: Math.max(0, project.total_filament_used - filamentUsed),
      total_print_time: Math.max(0, project.total_print_time - printTime)
    } as any)
  }

  const setCurrentProject = (project: Project | null) => { currentProject.value = project }

  // ─── Project Files (many-to-many) ────────────────────────

  const projectFiles = ref<Map<string, ProjectFile[]>>(new Map())

  const getProjectFiles = async (projectId: string): Promise<ProjectFile[]> => {
    const authStore = useAuthStore()
    if (authStore.isDemoMode) return []

    try {
      const { supabase } = await import('@/services/supabase')
      const { data, error: err } = await (supabase.from('project_files' as any) as any)
        .select('id, project_id, file_id, added_at, file:user_files(*)')
        .eq('project_id', projectId)
        .order('added_at', { ascending: false })

      if (err) throw err
      const files = (data || []) as ProjectFile[]
      projectFiles.value = new Map(projectFiles.value).set(projectId, files)
      return files
    } catch (err) {
      console.error('[projectStore] Failed to fetch project files:', err)
      return []
    }
  }

  const addFileToProject = async (projectId: string, fileId: string) => {
    try {
      const { supabase } = await import('@/services/supabase')
      const { error: err } = await (supabase.from('project_files' as any) as any)
        .insert({ project_id: projectId, file_id: fileId })

      if (err) throw err
      await getProjectFiles(projectId) // refresh
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to add file' }
    }
  }

  const removeFileFromProject = async (projectId: string, fileId: string) => {
    try {
      const { supabase } = await import('@/services/supabase')
      const { error: err } = await (supabase.from('project_files' as any) as any)
        .delete()
        .eq('project_id', projectId)
        .eq('file_id', fileId)

      if (err) throw err
      await getProjectFiles(projectId) // refresh
      return { success: true }
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : 'Failed to remove file' }
    }
  }

  return {
    projects, currentProject, loading, error, _loaded, _contextKey,
    activeProjects, completedProjects, projectsByStatus,
    totalFilamentUsed, totalPrintTime, overallSuccessRate, getById,
    fetchProjects, fetchProject, createProject, updateProject, deleteProject,
    updateStatus, addPrintToProject, removePrintFromProject, setCurrentProject,
    projectFiles, getProjectFiles, addFileToProject, removeFileFromProject,
    clearData
  }
})
